import React, { useState, useEffect, forwardRef } from 'react';
import MaterialTable from 'material-table';
import { useAuth } from '../../context/auth';
import { useMessage } from '../../context/message';
import { useField } from '../../context/context';
import Box from '@material-ui/core/Box';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import CustomFilter from '../CustomFilter';
import RefreshIcon from '@material-ui/icons/Refresh';
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom';

// Storage report product configs — dropdown options for the Storage Reports table.
// The stored value is the config key; the label is shown to the user.
const storageReportProducts = [
  { key: 'crude', label: 'Crude Oil' },
  { key: 'gasoline', label: 'Gasoline' },
  { key: 'distillate', label: 'Distillate' },
  { key: 'ethanol', label: 'Ethanol' },
  { key: 'heavy_products', label: 'Heavy Products' },
  { key: 'chemicals', label: 'Chemicals' },
  { key: 'aromatics', label: 'Aromatics' },
  { key: 'agricultural', label: 'Agricultural Products' },
  { key: 'ngl_lpg', label: 'NGL/LPG' },
  { key: 'condensate', label: 'Condensate' },
  { key: 'refined_products', label: 'Refined Products' },
];

const storageReportProductLabels = storageReportProducts.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});

const railColumns = [
  { title: 'Subscription ID', field: 'subscription_id', editable: 'never' },
  { title: 'Name', field: 'name' },
  { title: 'Email', field: 'email' },
  { title: 'Include', field: 'include', lookup: { 0: 'N', 1: 'Y' } },
  { title: 'Company', field: 'company' },
  { title: 'Start Date', field: 'start_date', type: 'date' },
  { title: 'End Date', field: 'end_date', type: 'date' },
  { title: 'Trial', field: 'trial' },
  { title: 'Phone', field: 'phone' },
];

function ReportSubscriptionTable({ reportType, columns }) {
  const [data, setData] = useState([]);
  const [toggle, setToggle] = useState(false);
  const { auth } = useAuth();
  const { setMessage } = useMessage();

  useEffect(() => {
    fetch(`/api/subscriptions/${reportType}/all`, {
      method: 'GET',
      headers: { 'x-access-token': sessionStorage.getItem('token') }
    })
    .then(response => auth(response))
    .then(response => setData(response.data || []))
    .catch(error => {
      setMessage({ type: 'error', text: `Error! Unable to fetch ${reportType} report subscriptions` });
      console.error('Error:', error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggle]);

  const handleAdd = newData => new Promise((resolve, reject) => {
    fetch(`/api/subscriptions/${reportType}`, {
      method: 'POST',
      headers: {
        'x-access-token': sessionStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newData)
    })
    .then(response => auth(response))
    .then(response => {
      setMessage(response.message);
      if (response.message.type === 'success') { setToggle(t => !t); resolve(); }
      else reject();
    })
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to create subscription' });
      console.error('Error:', error);
      reject();
    });
  });

  const handleUpdate = (newData, oldData) => new Promise((resolve, reject) => {
    fetch(`/api/subscriptions/${reportType}/${oldData.subscription_id}`, {
      method: 'PUT',
      headers: {
        'x-access-token': sessionStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newData)
    })
    .then(response => auth(response))
    .then(response => {
      setMessage(response.message);
      if (response.message.type === 'success') { setToggle(t => !t); resolve(); }
      else reject();
    })
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to update subscription' });
      console.error('Error:', error);
      reject();
    });
  });

  const handleDelete = oldData => new Promise((resolve, reject) => {
    fetch(`/api/subscriptions/${reportType}/${oldData.subscription_id}`, {
      method: 'DELETE',
      headers: { 'x-access-token': sessionStorage.getItem('token') }
    })
    .then(response => auth(response))
    .then(response => {
      setMessage(response.message);
      if (response.message.type === 'success') { setToggle(t => !t); resolve(); }
      else reject();
    })
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to delete subscription' });
      console.error('Error:', error);
      reject();
    });
  });

  const title = reportType.charAt(0).toUpperCase() + reportType.slice(1) + ' Report Subscriptions';

  return (
    <MaterialTable
      title={title}
      columns={columns}
      data={data}
      icons={{ Export: forwardRef((props, ref) => <VerticalAlignBottomIcon color='primary' {...props} ref={ref} />) }}
      options={{
        filtering: true,
        draggable: false,
        exportButton: true,
        exportAllData: true,
        exportFileName: `${reportType}_report_subscriptions`,
      }}
      components={{ FilterRow: props => <CustomFilter {...props} /> }}
      actions={[
        {
          icon: () => <RefreshIcon color='primary' />,
          tooltip: 'Refresh',
          isFreeAction: true,
          onClick: () => setToggle(t => !t)
        }
      ]}
      editable={{
        onRowAdd: handleAdd,
        onRowUpdate: handleUpdate,
        onRowDelete: handleDelete
      }}
    />
  );
}

export default function Subscriptions() {
  const { regions } = useField();

  const storageColumns = [
    { title: 'Subscription ID', field: 'subscription_id', editable: 'never' },
    { title: 'Company', field: 'company' },
    { title: 'Email', field: 'email' },
    { title: 'First Name', field: 'first_name' },
    { title: 'Last Name', field: 'last_name' },
    { title: 'Phone', field: 'phone' },
    { title: 'Trial', field: 'trial', lookup: { 0: 'N', 1: 'Y' } },
    { title: 'Active', field: 'active', lookup: { 0: 'N', 1: 'Y' } },
    { title: 'Start Date', field: 'start_date', type: 'date' },
    { title: 'Renewal Date', field: 'renewal_date', type: 'date' },
    {
      title: 'Products',
      field: 'products',
      render: rowData => {
        const keys = rowData.products ? rowData.products.split(', ').filter(Boolean) : [];
        return keys.map(k => storageReportProductLabels[k] || k).join(', ');
      },
      editComponent: props => {
        const selected = props.value ? props.value.split(', ').filter(Boolean) : [];
        return (
          <Select
            multiple
            value={selected}
            onChange={e => props.onChange(e.target.value.join(', '))}
            renderValue={v => v.map(k => storageReportProductLabels[k] || k).join(', ')}
          >
            {storageReportProducts.map(item => (
              <MenuItem key={item.key} value={item.key}>
                <Checkbox checked={selected.indexOf(item.key) > -1} />
                {item.label}
              </MenuItem>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Regions',
      field: 'regions',
      editComponent: props => {
        const selected = props.value ? props.value.split(', ').filter(Boolean) : [];
        return (
          <Select
            multiple
            value={selected}
            onChange={e => props.onChange(e.target.value.join(', '))}
            renderValue={v => v.join(', ')}
          >
            {regions && regions.map(item => (
              <MenuItem key={item.region} value={item.region}>
                <Checkbox checked={selected.indexOf(item.region) > -1} />
                {item.region}
              </MenuItem>
            ))}
          </Select>
        );
      }
    },
  ];

  return (
    <div>
      <ReportSubscriptionTable reportType='rail' columns={railColumns} />
      <Box mt={3}>
        <ReportSubscriptionTable reportType='storage' columns={storageColumns} />
      </Box>
    </div>
  );
}
